import type { SubmitResponse } from "../api";
import { Diamond } from "./icons";
// Shared with the dashboard's lead page, so what Mastery reads before a call is
// word-for-word what the respondent was shown. See src/resultNarrative.ts.
import {
  CONTACT_LABEL,
  CONTACT_URL,
  FOCUS_LINE,
  NOT_A_VALUATION,
  SCORE_LABEL,
  bandStyle,
  focus,
  heat,
} from "../resultNarrative";

/** The site to return to when this is not running inside the Wix page. */
const SITE = "https://www.masterypartners.com";

/**
 * Hand the visitor back to the site they came from.
 *
 * Embedded, this asks the host page to do it: the snippet holds the URL and
 * navigates itself. The widget deliberately does not send a destination — a
 * cross-origin frame that can tell its host where to go is an open redirect,
 * and there is no reason to build one for this.
 *
 * Standalone — the bare app URL, which is how it gets demonstrated — there is
 * no host to ask, so it just goes to the site.
 */
function returnToSite(): void {
  const embedded = window.parent !== window;
  if (!embedded) {
    window.location.href = SITE;
    return;
  }
  try {
    window.parent.postMessage({ type: "tra-return" }, "*");
  } catch {
    window.location.href = SITE;
  }
}

export function Results({
  result,
  email,
  onRestart,
}: {
  result: SubmitResponse;
  email: string;
  onRestart: () => void;
}) {
  const style = bandStyle(result.band);
  const focused = focus(result.categories);

  return (
    <>
      <div className="band-hero" style={{ background: style.bg, color: style.ink }}>
        <div className="band-label">{result.band}</div>
        <div className="band-score">
          <span className="band-score-label">{SCORE_LABEL}</span>{" "}
          <span className="band-score-value">{result.overall_score.toFixed(1)}%</span>
        </div>
        {style.say && <p className="band-say">{style.say}</p>}
      </div>

      <h2 className="q-single-text" style={{ fontSize: 18, marginBottom: 18 }}>
        How you scored by area
      </h2>
      <div className="catbars">
        {result.categories.map((c) => (
          <div className="catbar-row" key={c.category}>
            <span className="catbar-name">{c.category}</span>
            <span className="catbar-val">{c.score.toFixed(1)}%</span>
            <span className="catbar-track">
              <span
                className="catbar-fill"
                style={{ width: `${c.score}%`, background: heat(c.score) }}
              />
            </span>
          </div>
        ))}
      </div>

      {focused && (
        <div className="result-note">
          <span style={{ color: "var(--citron-ink)", flexShrink: 0, marginTop: 2 }}>
            <Diamond />
          </span>
          <div>
            {FOCUS_LINE[0]}
            <b>{focused.strongest.category.toLowerCase()}</b>
            {FOCUS_LINE[1]}
            <b>{focused.weakest.category.toLowerCase()}</b>
            {FOCUS_LINE[2]}
          </div>
        </div>
      )}

      <div className="result-note" style={{ background: "var(--accent-wash)" }}>
        <span style={{ color: "var(--citron-ink)", flexShrink: 0, marginTop: 2 }}>
          <Diamond />
        </span>
        <div>
          {result.report_email_queued ? (
            <>
              <b>Check your inbox.</b> A copy of these results is on its way to <b>{email}</b>,
              with what each level means.
            </>
          ) : (
            <>
              <b>Your results are saved.</b> We&rsquo;ll be in touch at <b>{email}</b> with the full
              breakdown shortly.
            </>
          )}
        </div>
      </div>

      <a
        className="btn btn-full"
        href={CONTACT_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
      >
        {CONTACT_LABEL}
      </a>
      <button className="btn btn-ghost btn-full" onClick={returnToSite} style={{ marginTop: 10 }}>
        Back to Mastery Partners
      </button>
      <button className="btn-quiet" onClick={onRestart} style={{ marginTop: 4 }}>
        Start over
      </button>

      <p className="gate-assure">
        <span style={{ color: "var(--citron-ink)", flexShrink: 0, marginTop: 2 }}>
          <Diamond />
        </span>
        <span>{NOT_A_VALUATION}</span>
      </p>
    </>
  );
}
